import useReveal from "@/hooks/useReveal";

const steps = [
  {
    num: "01",
    title: "Book the appointment",
    desc: "Schedule patients directly within LumeroFlow's calendar system.",
  },
  {
    num: "02",
    title: "Run the consultation",
    desc: "See the patient with their full record and history at hand.",
  },
  {
    num: "03",
    title: "Record or upload the session",
    desc: "Capture the consultation audio for AI-powered documentation.",
  },
  {
    num: "04",
    title: "Generate and review the note",
    desc: "AI drafts structured notes for the clinician to review and approve.",
  },
  {
    num: "05",
    title: "Save everything into the patient record",
    desc: "Notes, invoices, and visit data are stored together automatically.",
  },
];

const HowItWorksSection = () => {
  const [ref, isVisible] = useReveal();

  return (
    <section
      ref={ref}
      id="how-it-works"
      data-testid="how-it-works-section"
      className="py-20 md:py-32"
      style={{ backgroundColor: "#FAF9F6" }}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-start">
          {/* Left: heading + image */}
          <div className={`reveal-section ${isVisible ? "visible" : ""}`}>
            <p className="text-sm font-medium uppercase tracking-[0.2em] text-[#205B5A] mb-5">
              Workflow
            </p>
            <h2
              data-testid="how-it-works-heading"
              className="text-3xl sm:text-4xl md:text-5xl font-medium tracking-tight leading-tight text-[#1A1B1A] mb-8"
              style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
            >
              How LumeroFlow works
            </h2>
            <p className="text-base md:text-lg text-[#4A4B4A] leading-relaxed mb-10 max-w-md">
              From booking to completed documentation, everything flows through one connected system.
            </p>
            <div className="rounded-2xl overflow-hidden border border-[#1A1B1A]/[0.06] shadow-sm">
              <img
                src="https://static.prod-images.emergentagent.com/jobs/7f408a0d-f1e3-4dc6-b855-a5d9ce814faf/images/8b4d7c530b3e527084f8165acc23e77625271ba0bb2f20b6ab8e6e547593f086.png"
                alt="Patient record and AI notes interface"
                className="w-full h-auto"
                data-testid="how-it-works-image"
              />
            </div>
          </div>

          {/* Right: steps */}
          <div className="space-y-0">
            {steps.map((step, i) => (
              <div
                key={step.num}
                data-testid={`step-${step.num}`}
                className={`step-connector py-8 first:pt-0 last:pb-0 reveal-section ${isVisible ? "visible" : ""}`}
                style={{ transitionDelay: `${i * 0.12}s` }}
              >
                <div className="flex gap-6">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#205B5A] flex items-center justify-center">
                    <span className="text-xs font-semibold text-white">{step.num}</span>
                  </div>
                  <div className="pt-1">
                    <h3
                      className="text-lg md:text-xl font-medium text-[#1A1B1A] mb-2"
                      style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
                    >
                      {step.title}
                    </h3>
                    <p className="text-sm md:text-base text-[#4A4B4A] leading-relaxed">
                      {step.desc}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorksSection;
